import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  PiInfoDuotone as InfoIcon,
  PiPlusBold as Plus,
  PiRulerDuotone as UnitsIcon,
  PiTrashDuotone as Trash2,
} from "react-icons/pi";
import { ActionIcon, Alert, Badge, Button, Checkbox, Input, Radio } from "rizzui";
import FormFooter from "@core/components/form-footer";
import { FormGroup, cn } from "@steelyard/theme";
import { SelectField } from "./select-field";
import { SectionTitle } from "./section-title";
import { formCard, formCardFooter, formSections } from "../lib/form-layout";
import type { SellableUnitInput, SkuUnitRow, UnitOption } from "../lib/services/catalog";

type UnitRow = { unitId: string; unitCode: string; conversionToStocking: string; isPrimary: boolean; isSellable: boolean };
type FormValues = { units: UnitRow[]; unitToAdd: string };

function toRows(initialUnits: readonly SkuUnitRow[]): UnitRow[] {
  return initialUnits.map((u) => ({
    unitId: u.unitId,
    unitCode: u.unitCode,
    conversionToStocking: String(u.conversionToStocking),
    isPrimary: u.isPrimary,
    isSellable: u.isSellable,
  }));
}

/**
 * The Piece unit's fixed id — `Unit.PieceUnitId` in the backend seed. Matched by id, never by code
 * or name: the code was renamed NUM → PCS (2026-10-08) and a code match silently stopped working.
 */
const PIECE_UNIT_ID = "0a5c3f10-0000-4000-8000-000000000001";

/** FR-011: Piece is always present. Force-appended if the server's own set left it out. */
function withPieceUnit(rows: UnitRow[], pieceUnit: UnitOption | undefined): UnitRow[] {
  if (!pieceUnit || rows.some((r) => r.unitId === PIECE_UNIT_ID)) return rows;
  return [...rows, { unitId: pieceUnit.id, unitCode: pieceUnit.code, conversionToStocking: "", isPrimary: false, isSellable: true }];
}

/** The one grid every row and the header share, so the columns line up. */
const GRID = "grid grid-cols-[minmax(10rem,1.2fr)_minmax(15rem,1.6fr)_5.5rem_7.5rem_2.5rem] items-center gap-x-4";

/**
 * T050/T051/T052 (catalogue-sku-editor) — tasks.md marked T050 `[X]` "done" but
 * `components/sku-unit-editor.tsx` does not exist anywhere in the repo or its git history; this is
 * the real, first build of it, a prerequisite for T051's Units screen rather than something T051
 * composes pre-built.
 *
 * Rebuilt on the same repeatable-row pattern `components/orders/delivery-zone-editor.tsx` already
 * ports from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/product/create-edit/shipping-info.tsx
 * — `useFieldArray`, an input per column, a per-row `ActionIcon` + trash icon, an "Add" control —
 * inside the form shell `discount-rule-builder.tsx` uses (`formCard`/`formSections`/`FormFooter`).
 *
 * 2026-09-29 (owner: "too basic and messy, say what this is for"): one aligned table with a
 * single header row instead of a label on every input, each row reading as the sentence it
 * stores — "1 PCS = [7.4] KGS" — and a short note on what a stocking unit, a conversion, the
 * primary unit and "offered for sale" mean. The accessible names the tests and screen readers use
 * ("Conversion to stocking unit", "Primary", "Offered for sale") are kept on each control.
 *
 * FR-011: the Piece unit (`PIECE_UNIT_ID`, confirmed against the backend seed —
 * `UnitConfiguration.cs`'s `(Unit.PieceUnitId, "PCS", "Piece", UnitType.Count, 0)`) is always
 * present and can never be removed — force-appended before render if the server's set omitted it,
 * no trash icon on its row, excluded from the "add a unit" picker.
 * FR-010: exactly one primary — a native radio group makes a second primary structurally
 * impossible, the same intent the original (never-shipped) T042 stated.
 * FR-012: `conversionToStocking` must be `> 0` — checked on submit, inline error on the offending
 * row. This is a client-side convenience only; the API's own 422 (`detail` naming the rule) is what
 * actually decides (data-model.md §2) and is rendered verbatim via the `error` prop (T052) — this
 * component does not parse `ApiError` itself, the page that owns the API call does.
 * §7.1 `is_sellable`: each row has an "Offered for sale" checkbox, so a stocking-only unit can be
 * recorded without being sold in. Piece's is fixed on (FR-011); the API's 422 decides the rest
 * (e.g. a primary unit that is not offered) and is shown verbatim.
 */
export function SkuUnitEditor({
  initialUnits,
  units,
  stockingUnitId,
  onSave,
  onCancel,
  busy,
  error,
}: {
  initialUnits: readonly SkuUnitRow[];
  units: readonly UnitOption[];
  /** The SKU's stocking unit — every conversion is stated in it. */
  stockingUnitId?: string;
  onSave: (rows: SellableUnitInput[]) => void;
  onCancel?: () => void;
  busy: boolean;
  /** The API's own 422 rule name, verbatim (T052) — set by the page that owns the `onSave` call. */
  error?: string;
}) {
  const pieceUnit = units.find((u) => u.id === PIECE_UNIT_ID);
  const unitById = new Map(units.map((u) => [u.id, u]));
  const stockingCode = (stockingUnitId && unitById.get(stockingUnitId)?.code) ?? "stocking unit";
  // The note's example is this SKU's own first other unit, as saved — not a made-up figure.
  const example = initialUnits.find((u) => u.unitId !== stockingUnitId && u.conversionToStocking > 0);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { units: withPieceUnit(toRows(initialUnits), pieceUnit), unitToAdd: "" },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "units" });

  // Resyncs after a save — the server now holds exactly `initialUnits`, and a stale "add a unit"
  // scratch row must not linger past a successful save (same reasoning as DeliveryZoneEditor).
  useEffect(() => {
    reset({ units: withPieceUnit(toRows(initialUnits), pieceUnit), unitToAdd: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUnits]);

  const watchedRows = watch("units");
  const unitToAdd = watch("unitToAdd");
  const chosenIds = new Set(watchedRows.map((r) => r.unitId));
  const addableUnits = units.filter((u) => u.id !== PIECE_UNIT_ID && !chosenIds.has(u.id));

  function addUnit() {
    const unit = units.find((u) => u.id === unitToAdd);
    if (!unit) return;
    append({ unitId: unit.id, unitCode: unit.code, conversionToStocking: "", isPrimary: false, isSellable: true });
    setValue("unitToAdd", "");
  }

  function makePrimary(index: number) {
    fields.forEach((_, i) => setValue(`units.${i}.isPrimary`, i === index));
  }

  const submit = handleSubmit((values) => {
    clearErrors();
    let hasError = false;
    const out: SellableUnitInput[] = [];

    values.units.forEach((row, index) => {
      const conversionToStocking = Number(row.conversionToStocking);
      const message = !row.conversionToStocking.trim()
        ? "Required"
        : !Number.isFinite(conversionToStocking)
          ? "Enter a number"
          : conversionToStocking <= 0
            ? "Must be greater than 0"
            : null;
      if (message) {
        setError(`units.${index}.conversionToStocking`, { message });
        hasError = true;
      }
      out.push({
        unitId: row.unitId,
        conversionToStocking,
        isPrimary: row.isPrimary,
        // A disabled input may read back undefined; Piece is always offered (FR-011).
        isSellable: row.unitId === PIECE_UNIT_ID || Boolean(row.isSellable),
      });
    });

    // The API's own rule (exactly one primary) — said here, before the round trip, since a fresh
    // SKU starts with no primary and nothing on screen says one is required.
    if (!out.some((row) => row.isPrimary)) {
      setError("root", { message: "Choose which unit is primary." });
      hasError = true;
    }

    if (hasError) return;
    onSave(out);
  });

  return (
    <form onSubmit={submit} className={formCard}>
      <div className={formSections}>
        <FormGroup
          title={<SectionTitle icon={UnitsIcon}>Sellable units</SectionTitle>}
          description="The units a customer can buy this SKU in, and how much stock each one uses."
        >
          <Alert color="info" variant="flat" icon={<InfoIcon aria-hidden className="size-5" />} className="col-span-full">
            <ul className="list-disc space-y-1 ps-4 text-sm">
              <li>
                Stock is counted in <strong>{stockingCode}</strong>, the stocking unit. Each row says how much of it one
                unit uses
                {example ? (
                  <>
                    {" "}— <em>
                      1 {example.unitCode} = {example.conversionToStocking} {stockingCode}
                    </em>{" "}
                    means selling one {example.unitCode} takes {example.conversionToStocking} {stockingCode} off stock.
                  </>
                ) : (
                  "."
                )}
              </li>
              <li>
                The <strong>primary</strong> unit is the one the storefront shows first. Exactly one unit is primary.
              </li>
              <li>
                Untick <strong>Offered for sale</strong> to keep a unit for stock and conversions only. Piece (PCS) is
                always included and always offered.
              </li>
              <li>Each unit is priced separately, on Base prices.</li>
            </ul>
          </Alert>

          <div className="col-span-full flex flex-col gap-3">
            {error || errors.root?.message ? (
              <p role="alert" className="text-sm text-red-dark">
                {error ?? errors.root?.message}
              </p>
            ) : null}

            <div className="overflow-x-auto">
              <div className="min-w-[44rem] rounded-lg border border-muted">
                <div
                  aria-hidden
                  className={cn(GRID, "rounded-t-lg border-b border-muted bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500")}
                >
                  <span>Unit</span>
                  <span>One unit equals</span>
                  <span className="text-center">Primary</span>
                  <span className="text-center">Offered for sale</span>
                  <span />
                </div>

                {fields.map((item, index) => {
                  const unit = unitById.get(item.unitId);
                  const isPiece = item.unitId === PIECE_UNIT_ID;
                  const conversionError = errors.units?.[index]?.conversionToStocking?.message;
                  return (
                    <div key={item.id} className={cn(GRID, "border-b border-muted px-4 py-3 last:border-b-0")}>
                      <input type="hidden" {...register(`units.${index}.unitId`)} />
                      <input type="hidden" {...register(`units.${index}.unitCode`)} />
                      <input type="hidden" {...register(`units.${index}.isPrimary`)} />

                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="truncate font-medium text-gray-900">{unit?.name ?? item.unitCode}</span>
                        <span className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="flat" color="secondary" rounded="md" size="sm" className="font-mono">
                            {item.unitCode}
                          </Badge>
                          {item.unitId === stockingUnitId ? (
                            <Badge variant="flat" color="info" rounded="md" size="sm">
                              Stocking unit
                            </Badge>
                          ) : null}
                        </span>
                      </div>

                      <div className="flex min-w-0 flex-col gap-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="shrink-0 font-mono">1 {item.unitCode} =</span>
                          <Input
                            aria-label="Conversion to stocking unit"
                            inputMode="decimal"
                            className="w-28 shrink-0"
                            inputClassName="font-mono"
                            {...register(`units.${index}.conversionToStocking`)}
                            error={conversionError ? " " : undefined}
                          />
                          <span className="shrink-0 font-mono">{stockingCode}</span>
                        </div>
                        {conversionError ? (
                          <p role="alert" className="text-[13px] text-red">
                            {conversionError}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex justify-center">
                        <Radio
                          aria-label="Primary"
                          name="primary-unit"
                          checked={watchedRows[index]?.isPrimary ?? false}
                          onChange={() => makePrimary(index)}
                        />
                      </div>

                      <div className="flex justify-center" title={isPiece ? "Piece is always offered for sale." : undefined}>
                        <Checkbox aria-label="Offered for sale" disabled={isPiece} {...register(`units.${index}.isSellable`)} />
                      </div>

                      <div className="flex justify-end">
                        {isPiece ? null : (
                          <ActionIcon type="button" variant="flat" size="sm" onClick={() => remove(index)} aria-label={`Remove ${item.unitCode}`}>
                            <Trash2 aria-hidden className="size-4" />
                          </ActionIcon>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <SelectField
                label="Add a unit"
                wrapperClassName="w-full max-w-xs"
                disabled={addableUnits.length === 0}
                disabledReason={addableUnits.length === 0 ? "Every unit is already on this SKU." : undefined}
                {...register("unitToAdd")}
              >
                <option value="">{addableUnits.length === 0 ? "No units left to add" : "Choose a unit…"}</option>
                {addableUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </SelectField>
              <Button type="button" variant="outline" disabled={!unitToAdd} onClick={addUnit}>
                <Plus aria-hidden className="me-2 size-4" strokeWidth={2} />
                Add unit
              </Button>
            </div>
            <p className="text-xs text-gray-500">Saving replaces the whole set with exactly these rows.</p>
          </div>
        </FormGroup>
      </div>

      <FormFooter
        className={formCardFooter}
        submitBtnText={busy ? "Saving…" : "Save sellable units"}
        isLoading={busy}
        altBtnText="Cancel"
        handleAltBtn={onCancel}
      />
    </form>
  );
}
